import { useMutation, useQuery, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { client, getRxFilePreviewUrl } from '../../api';
import type { AcceptPriorAuthReviewData, PriorAuthReviewFormData, PriorAuthReviewFormUpdateData } from '../../api';
import { Sidebar, SidebarTopBar } from '../sidebar/Sidebar';
import { ItemViewTopNav } from '../tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../tableItemDetailViewComponents/Containers';
import React, { useContext, useEffect, useState, useId } from 'react';
import { DrugPanel, ICDCodePanel, PatientPanel, PrescriberPanel } from '../sidebar/SidebarPanels';
import styled from 'styled-components';
import { PriorAuthReviewContext } from './contexts';
import Button from 'design-system/src/components/Button/Button';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import { TimelineItemList, TimelineItem } from '../TimelineItem/TimelineItem';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import toast from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { pageLevelErrorCopy } from 'design-system/src/sharedCopy';
import { CustomPaForm } from './CustomPaForm/CustomPaForm';
import { GeneralInfoTable } from '../GeneralInfoTable/GeneralInfoTable';
import { StyledContainer } from '../tableItemDetailViewComponents/Containers';
import { PDFViewer } from '../PDFViewer';
import { FloatingEditButton } from '../FloatingEditButton/FloatingEditButton';
import LetterOfMedicalNecessityEditModal from '../LmnEditModal';

import { PROVIDER_SUPPORT_CONTACT_INFO } from '../../constants';
import { RadioBlockGroup, RadioLabel, RadioOptionWrapper } from 'design-system/src/components/Radio/Radio';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';

const FORM_FOOTER_HEIGHT = 66;
// Arbitrary height to allow submission button to be visible at bottom
const LMN_FORM_HEIGHT = '400px';
const FORM_SECTION_ID = 'review-pa-form-heading';
const LMN_SECTION_ID = 'review-lmn-form-heading';

const StyledCustomPaFormViewer = styled.div<{ $hasIframe: boolean }>`
  overflow-y: ${({ $hasIframe }) => ($hasIframe ? 'hidden' : 'scroll')};
  border-radius: var(--border-radius-medium);
  height: 80dvh;
`;

const NoEmbedUrlErrorMessage = styled(Typography)`
  padding: 16px;
`;
const StyledIframe = styled.iframe`
  border: none;
  border-radius: var(--border-radius-medium);
  height: 100%;
  width: 100%;
  // the iframe contents scroll on their own inside the iframe
  // TODO: test this on ios
  overflow-y: hidden;
`;

// Styled components for form tree PA review footer
const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
  height: ${FORM_FOOTER_HEIGHT}px;
  box-sizing: border-box;
  border-top: 1px solid var(--light-border-gray);
  background: var(--white);
  border-bottom-left-radius: var(--border-radius-medium);
  border-bottom-right-radius: var(--border-radius-medium);
  position: relative;
`;
const FooterLoadingSpinner = styled(LoadingSpinner)`
  width: 1.5rem;
  // Horizontally center the spinner regardless of the other
  // buttons in the footer
  position: absolute;
  left: calc(50% - 0.75rem);
`;

const FormAndFooterContainer = styled(StyledContainer)`
  margin: 0 4px 32px;
`;
const LMNFormContainer = styled(StyledContainer)`
  margin: 12px 4px 0;
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
`;
const SubmitButton = styled(Button)`
  margin: 32px 0;
`;

type FormTreePaReviewFooterProps = {
  isFormComplete: boolean;
  hasLetterOfMedicalNecessity: boolean;
  isSavingAnswers: boolean;
  isSubmittingReview: boolean;
  attestationCheckboxErrorMessageId: string;
  resetMutation: UseMutationResult<void, Error, void>;
  onFormComplete: () => void;
};

/**
 * Footer component for form tree PA reviews that contains reset and submit buttons
 */
const FormTreePaReviewFooter: React.FC<FormTreePaReviewFooterProps> = ({
  isFormComplete,
  hasLetterOfMedicalNecessity,
  isSavingAnswers,
  isSubmittingReview,
  attestationCheckboxErrorMessageId,
  resetMutation,
  onFormComplete,
}) => {
  return (
    <FormFooter>
      <Button
        variant="tertiary"
        size="sm"
        onClick={() => {
          if (!isSubmittingReview && !resetMutation.isPending) {
            resetMutation.mutate();
          }
        }}
        isLoading={resetMutation.isPending}
      >
        Reset original answers
      </Button>

      {isSavingAnswers && <FooterLoadingSpinner />}

      {isFormComplete ? (
        <Button
          size="sm"
          onClick={onFormComplete}
          isLoading={isSubmittingReview}
          aria-describedby={attestationCheckboxErrorMessageId}
        >
          {hasLetterOfMedicalNecessity ? 'Next' : 'Accept'}
        </Button>
      ) : (
        // TODO (ahu): only show error when user tries to continue
        <ErrorMessage>Please complete the form before proceeding.</ErrorMessage>
      )}
    </FormFooter>
  );
};

/**
 * Submit button component that appears at the bottom of the page.
 * Only appears in some cases -- see shouldShowBottomSubmitButton.
 */
const BottomPageSubmitButton: React.FC<{
  isLoading: boolean;
  onSubmit: () => void;
}> = ({ isLoading, onSubmit }) => {
  return (
    <SubmitButton width="full" onClick={onSubmit} isLoading={isLoading}>
      Submit PA information
    </SubmitButton>
  );
};

/**
 * Determines whether to show the BottomPageSubmitButton component
 * at the bottom of the page.
 *
 * The button should be shown when:
 * 1. The form has not been submitted AND either
 * 2a. The PA review is a pandadoc form that has been signed already
 *    (provider signed outside of the portal, or user navigated away
 *     too quickly, so review was not auto-submitted)
 * 2b. OR There is a Letter of Medical Necessity to review
 */
export const shouldShowSignatureOrLMNSubmitButton = ({
  formWasSubmitted,
  hasLetterOfMedicalNecessity,
  signatureRequired,
  signatureCompleted,
}: {
  formWasSubmitted: boolean;
  hasLetterOfMedicalNecessity: boolean;
  signatureRequired: boolean;
  signatureCompleted: boolean;
}): boolean => {
  if (formWasSubmitted) {
    return false;
  }
  if (signatureRequired && signatureCompleted) {
    return true;
  }
  return hasLetterOfMedicalNecessity;
};

const LetterOfMedicalNecessityForm = ({
  rxId,
  documentId,
  includeLMN,
  setIncludeLMN,
  setShowLMNEditModal,
  formDisabled,
}: {
  rxId: string;
  documentId: string;
  includeLMN: boolean;
  setIncludeLMN: (includeLMN: boolean) => void;
  setShowLMNEditModal: (show: boolean) => void;
  formDisabled: boolean;
}) => {
  const acceptLMNOptionId = useId();
  const declineLMNOptionId = useId();
  return (
    <div>
      <Typography styledAs="bodySmallSpaceGrotesk" renderedAs="h3" marginBottom="8px" weight="500" id={LMN_SECTION_ID}>
        Review Letter of Medical Necessity
      </Typography>
      <Typography styledAs="bodySmallDMSans">
        Tandem's AI identified that this PA may be denied without additional information regarding lifestyle
        modification. Please review the Letter of Medical Necessity below to submit with the PA.
      </Typography>
      <LMNFormContainer>
        <PDFViewer pdfUrl={getRxFilePreviewUrl(rxId, documentId)} height={LMN_FORM_HEIGHT} />
        <FloatingEditButton top="350px" right="32px" onClick={() => setShowLMNEditModal(true)} />
        <RadioBlockGroup $variant="outlined">
          <RadioOptionWrapper
            id={acceptLMNOptionId}
            name="lmn-accept"
            value="lmn-accept"
            checked={includeLMN}
            onChange={() => setIncludeLMN(true)}
            variant="outlined"
            disabled={formDisabled}
          >
            <RadioLabel $disabled={formDisabled}>Include Letter of Medical Necessity in PA submission</RadioLabel>
          </RadioOptionWrapper>
          <RadioOptionWrapper
            id={declineLMNOptionId}
            name="lmn-decline"
            value="lmn-decline"
            checked={!includeLMN}
            onChange={() => setIncludeLMN(false)}
            variant="outlined"
            disabled={formDisabled}
          >
            <RadioLabel $disabled={formDisabled}>Do not include Letter of Medical Necessity</RadioLabel>
          </RadioOptionWrapper>
        </RadioBlockGroup>
      </LMNFormContainer>
    </div>
  );
};

// TODO: consider splitting out form tree vs. signature review into separate views
const PriorAuthReviewView: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [attestationCheckboxIsChecked, setAttestationCheckboxIsChecked] = useState(false);
  const [showAttestationCheckboxError, setShowAttestationCheckboxError] = useState(false);
  const [includeLMN, setIncludeLMN] = useState(true);
  // Used so we can update document shown from LMN edit flow
  const [lmnDocumentId, setLmnDocumentId] = useState<string | null>(null);
  const [lmnEditableContent, setLmnEditableContent] = useState<string | null>(null);
  const [showLMNEditModal, setShowLMNEditModal] = useState(false);

  const attestationCheckboxErrorMessageId = 'prior-auth-form-attestation-checkbox-error-message';
  const formId = 'custom-pa-form-viewer';

  const queryClient = useQueryClient();

  const { priorAuthReviewQuery, priorAuthReviewId, onPriorAuthReviewUpdate } = useContext(PriorAuthReviewContext);

  const handlePandadocMessage = (event: MessageEvent) => {
    const type = event.data?.type;
    const payload = event.data?.payload;

    if (type === 'session_view.document.completed') {
      const lmnSection = document.getElementById(LMN_SECTION_ID);
      if (lmnSection) {
        // scroll to LMN section instead, if it exists
        lmnSection.scrollIntoView({ behavior: 'smooth' });
      } else {
        // manual attestation isn't needed if the pa review is signed by the user
        acceptPriorAuthReview.mutate({ signature_required: true });
      }
    }
    if (type === 'session_view.document.exception') {
      console.error(payload);
      toast.custom(() => <StandardErrorToast />);
    }
  };

  useEffect(() => {
    window.addEventListener('message', handlePandadocMessage);

    return () => {
      window.removeEventListener('message', handlePandadocMessage);
    };
  }, []);

  const formQuery = useQuery<PriorAuthReviewFormData>({
    queryKey: ['customPaFormViewer', priorAuthReviewId],
    enabled: !!(priorAuthReviewQuery.data && !priorAuthReviewQuery.data.prior_auth_review.signature_required),
    queryFn: async () => {
      return client.get(`prior-auth-review/${priorAuthReviewId}/form`).then((res) => res.data);
    },
  });

  const saveQuestionAnswer = useMutation<PriorAuthReviewFormData, AxiosError, PriorAuthReviewFormUpdateData>({
    mutationFn: async (data) => {
      const res = await client.post(`prior-auth-review/${priorAuthReviewId}/form/answer`, data);
      return res.data;
    },
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Form saved</Toast>);
      return queryClient.invalidateQueries({ queryKey: ['customPaFormViewer', priorAuthReviewId] });
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const resetPriorAuthReviewForm = useMutation({
    mutationFn: async () => {
      const res = await client.post(`prior-auth-reviews/${priorAuthReviewId}/form/reset`);
      return res.data;
    },
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Form cleared</Toast>);
      return queryClient.invalidateQueries({ queryKey: ['customPaFormViewer', priorAuthReviewId] });
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const navigate = useNavigate();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const acceptPriorAuthReview = useMutation<boolean, AxiosError, { signature_required: boolean; lmn_id?: string }>({
    mutationFn: async ({ signature_required, lmn_id }) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking PA Review Accept request for ID: ${priorAuthReviewId}`);

        return Promise.resolve(true); // Simulate successful API response
      }
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking PA Review Accept request for ID: ${priorAuthReviewId}`);

        return Promise.resolve(true); // Simulate successful API response
      }

      let data: AcceptPriorAuthReviewData = {
        attestation_submitted: attestationCheckboxIsChecked || signature_required,
      };
      if (lmn_id) {
        data.letter_of_medical_necessity_id = lmn_id;
        data.letter_of_medical_necessity_status = includeLMN ? 'approved' : 'rejected';
      }
      const res = await client.post(`prior-auth-reviews/${priorAuthReviewId}/form/submit`, data);
      return res.data;
    },
    onSuccess: (signature_required) => {
      onPriorAuthReviewUpdate();
      navigate('/prior-auth-reviews');
      if (!signature_required) {
        return queryClient.invalidateQueries({ queryKey: ['customPaFormViewer', priorAuthReviewId] });
      }
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  let body;
  if (priorAuthReviewQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (priorAuthReviewQuery.error) {
    body = <PageLoadError />;
  } else if (priorAuthReviewQuery.data) {
    const { prior_auth_review, patient, prescriber, drug, icd_code, letter_of_medical_necessity } =
      priorAuthReviewQuery.data;

    let formBody;
    let formInstructions;
    const hasLetterOfMedicalNecessity = !!letter_of_medical_necessity;
    const formWasSubmitted = prior_auth_review.status === 'submitted';
    const showBottomSubmitButton = shouldShowSignatureOrLMNSubmitButton({
      formWasSubmitted,
      hasLetterOfMedicalNecessity,
      signatureRequired: prior_auth_review.signature_required,
      signatureCompleted: prior_auth_review.signature_completed,
    });

    if (prior_auth_review.signature_required) {
      if (prior_auth_review.embed_document_url) {
        formBody = <StyledIframe src={prior_auth_review.embed_document_url} />;
      } else {
        Sentry.captureException(new Error('No embed_document_url found for prior auth review with signature_required'));

        formBody = (
          // This should never happen and eng will be needed to unblock the user, hence
          // the custom error message with support's email
          <NoEmbedUrlErrorMessage styledAs="bodySmallDMSans">
            Error, please reach out to Tandem at
            <Button href={`mailto:${PROVIDER_SUPPORT_CONTACT_INFO.email}`} variant="link">
              {PROVIDER_SUPPORT_CONTACT_INFO.email}
            </Button>
          </NoEmbedUrlErrorMessage>
        );
      }
      if (!formWasSubmitted && prior_auth_review.signature_completed) {
        formInstructions = (
          <Typography styledAs="bodySmallDMSans" marginBottom="16px">
            Form has been signed. Click "Submit" below to complete this PA review.
          </Typography>
        );
      } else {
        formInstructions = (
          <Typography styledAs="bodySmallDMSans" marginBottom="16px">
            Sign below or use this link to sign outside of the Tandem portal:
            <Button
              href={prior_auth_review.embed_document_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="link"
            >
              {prior_auth_review.embed_document_url} <span className="sr-only">opens in a new tab</span>
            </Button>
          </Typography>
        );
      }
    } else {
      if (formQuery.isLoading) {
        formBody = <LoadingSpinner />;
      }
      if (formQuery.error) {
        formBody = pageLevelErrorCopy;
      }
      if (formQuery.data) {
        formBody = (
          <CustomPaForm
            form={formQuery.data.form}
            formDisabled={formWasSubmitted}
            formId={formId}
            saveQuestionAnswer={saveQuestionAnswer.mutate}
            attestationCheckboxIsChecked={attestationCheckboxIsChecked}
            setAttestationCheckboxIsChecked={setAttestationCheckboxIsChecked}
            showAttestationCheckboxError={showAttestationCheckboxError}
            setShowAttestationCheckboxError={setShowAttestationCheckboxError}
            attestationCheckboxErrorMessageId={attestationCheckboxErrorMessageId}
            attestationSubmitted={prior_auth_review.attestation_submitted}
          />
        );
      }
    }
    const isFormComplete = formQuery.data?.form_status === 'FORM_STATUS_COMPLETED';

    const handleFormCompletion = (blockedByLMNView: boolean) => {
      // Only applies to PA reviews with form tree
      if (!prior_auth_review.signature_required) {
        if (acceptPriorAuthReview.isPending || resetPriorAuthReviewForm.isPending) {
          return;
        }
        // TODO: we should be moving focus when we scroll to sections
        if (!attestationCheckboxIsChecked || !isFormComplete) {
          if (!attestationCheckboxIsChecked) {
            setShowAttestationCheckboxError(true);
          }
          // Scroll to form section first
          const formSection = document.getElementById(FORM_SECTION_ID);
          if (formSection) {
            formSection.scrollIntoView({ behavior: 'smooth' });
          }
          // Scroll to the bottom of the form so the user can see the checkbox and error
          const formContainer = document.getElementById(formId);
          if (formContainer) {
            formContainer.scrollTop = formContainer.scrollHeight;
          }
          return;
        }
      }
      if (blockedByLMNView) {
        // Scroll to LMN section
        const lmnSection = document.getElementById(LMN_SECTION_ID);
        if (lmnSection) {
          lmnSection.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Submit PA
        acceptPriorAuthReview.mutate({
          signature_required: prior_auth_review.signature_required,
          lmn_id: letter_of_medical_necessity?.id,
        });
      }
    };

    body = (
      <>
        <BodyContainer>
          <TopNavAndContent>
            <ItemViewTopNav isSidebarCollapsed={isSidebarCollapsed} setIsSidebarCollapsed={setIsSidebarCollapsed} />

            <ContentOuterWrapper>
              <CenteredContent>
                <Typography styledAs="h6" renderedAs="h2" marginBottom="1rem">
                  PA
                </Typography>

                <GeneralInfoTable
                  patientName={`${patient.first_name} ${patient.last_name}`}
                  dob={patient.date_of_birth}
                  prescriberName={prescriber.name}
                  prescribedOn={drug.prescription_recieved_at}
                  prescriptionId={drug.id}
                  drugName={drug.name}
                />

                {hasLetterOfMedicalNecessity && (
                  <InfoBox includeIcon={false} noTopMargin variant="surface">
                    <Typography styledAs="bodySmallDMSans">
                      Please review the PA form and the Letter of Medical Necessity attached below.
                    </Typography>
                  </InfoBox>
                )}

                {/* This check is just to satisfy typescript. If the prior authorization
                  review has been reviewed, there should be reviewed_by and reviewed_at
                  properties */}
                {prior_auth_review.status === 'submitted' &&
                  prior_auth_review.reviewed_by &&
                  prior_auth_review.reviewed_at && (
                    <TimelineItemList>
                      <TimelineItem
                        key={'prior-authorization-review-acceptance'}
                        // This timeline always only has 1 item in it
                        isLastEventInList={true}
                        author={prior_auth_review.reviewed_by}
                        timestamp={prior_auth_review.reviewed_at}
                        action="accepted prior authorization"
                      />
                    </TimelineItemList>
                  )}

                <Typography
                  styledAs="bodySmallSpaceGrotesk"
                  renderedAs="h3"
                  marginBottom="8px"
                  weight="500"
                  id={FORM_SECTION_ID}
                >
                  {prior_auth_review.signature_required ? 'Review and sign PA form' : 'Review PA form'}
                </Typography>
                {formInstructions}

                <FormAndFooterContainer>
                  <StyledCustomPaFormViewer $hasIframe={prior_auth_review.signature_required} id={formId}>
                    {formBody}
                  </StyledCustomPaFormViewer>

                  {/* Form tree PA reviews have their own footer with reset and submit buttons */}
                  {!formWasSubmitted && !prior_auth_review.signature_required && (
                    <FormTreePaReviewFooter
                      isFormComplete={isFormComplete}
                      hasLetterOfMedicalNecessity={hasLetterOfMedicalNecessity}
                      isSavingAnswers={saveQuestionAnswer.isPending}
                      isSubmittingReview={acceptPriorAuthReview.isPending}
                      attestationCheckboxErrorMessageId={attestationCheckboxErrorMessageId}
                      resetMutation={resetPriorAuthReviewForm}
                      onFormComplete={() => handleFormCompletion(hasLetterOfMedicalNecessity)}
                    />
                  )}
                </FormAndFooterContainer>

                {hasLetterOfMedicalNecessity && (
                  <LetterOfMedicalNecessityForm
                    includeLMN={letter_of_medical_necessity.status === 'rejected' ? false : includeLMN}
                    setIncludeLMN={setIncludeLMN}
                    rxId={drug.id}
                    documentId={lmnDocumentId || letter_of_medical_necessity.document_id}
                    formDisabled={formWasSubmitted}
                    setShowLMNEditModal={setShowLMNEditModal}
                  />
                )}
                {showBottomSubmitButton && (
                  <BottomPageSubmitButton
                    onSubmit={() => handleFormCompletion(false)}
                    isLoading={acceptPriorAuthReview.isPending}
                  />
                )}
              </CenteredContent>
            </ContentOuterWrapper>
          </TopNavAndContent>

          <Sidebar $isCollapsed={isSidebarCollapsed}>
            <SidebarTopBar />
            <DrugPanel drug={drug} />
            {icd_code && <ICDCodePanel icdCode={icd_code} />}
            <PatientPanel patient={patient} />
            <PrescriberPanel prescriber={prescriber} />
          </Sidebar>
        </BodyContainer>

        {showLMNEditModal && !!letter_of_medical_necessity && (
          <LetterOfMedicalNecessityEditModal
            paReviewId={priorAuthReviewId}
            lmnId={letter_of_medical_necessity.id}
            letterContent={lmnEditableContent || letter_of_medical_necessity.editable_letter_content}
            closeModal={() => setShowLMNEditModal(false)}
            setDocumentId={setLmnDocumentId}
            setEditableContent={setLmnEditableContent}
          />
        )}
      </>
    );
  }

  return body;
};

export default PriorAuthReviewView;
